import cornerstone from 'cornerstone-core';
import cornerstoneTools from 'cornerstone-tools';
import OHIF from '@ohif/core';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import setCornerstoneLayout from './utils/setCornerstoneLayout.js';
import { getEnabledElement } from './state';
import CornerstoneViewportDownloadForm from './CornerstoneViewportDownloadForm';
import {
  buildPrintDocument,
  captureDisplaySetImages,
  getPrintHeader,
  getSeriesGrid,
  openPrintSheet,
  MAX_PAGES_WITHOUT_CONFIRM,
} from './utils/printSheet';
import PrintConfirmDialog from './utils/printSheet/PrintConfirmDialog';
const scroll = cornerstoneTools.import('util/scroll');

const { studyMetadataManager, StackManager } = OHIF.utils;
const { setViewportSpecificData } = OHIF.redux.actions;

const refreshCornerstoneViewports = () => {
  cornerstone.getEnabledElements().forEach(enabledElement => {
    if (enabledElement.image) {
      cornerstone.updateImage(enabledElement.element);
    }
  });
};

/** The displaySet-ish object the active viewport is showing. */
const _getActiveDisplaySetData = viewports =>
  (viewports.viewportSpecificData || {})[viewports.activeViewportIndex] || {};

const _getActiveImageId = activeViewportIndex => {
  const element = getEnabledElement(activeViewportIndex);
  if (!element) {
    return;
  }

  const enabledElement = cornerstone.getEnabledElement(element);
  return enabledElement && enabledElement.image
    ? enabledElement.image.imageId
    : undefined;
};

/**
 * imageIds of a series. `viewportSpecificData` cannot be used directly because
 * `ImageSet.images` is non enumerable and gets lost when the viewports reducer
 * clones the displaySet, so the stack is taken from the StackManager (already
 * built by the viewport) and rebuilt from the study metadata as a fallback.
 */
const _getImageIds = ({ displaySetInstanceUID, StudyInstanceUID }) => {
  if (!displaySetInstanceUID) {
    return [];
  }

  const stack = StackManager.findStack(displaySetInstanceUID);
  if (stack && stack.imageIds && stack.imageIds.length) {
    return stack.imageIds;
  }

  const studyMetadata = studyMetadataManager.get(StudyInstanceUID);
  if (!studyMetadata) {
    return [];
  }

  const displaySet = studyMetadata
    .getDisplaySets()
    .find(set => set.displaySetInstanceUID === displaySetInstanceUID);

  if (!displaySet) {
    return [];
  }

  const createdStack = StackManager.findOrCreateStack(
    studyMetadata.getData(),
    displaySet
  );

  return (createdStack && createdStack.imageIds) || [];
};

const commandsModule = ({ servicesManager }) => {
  const actions = {
    rotateViewport: ({ viewports, rotation }) => {
      const enabledElement = getEnabledElement(viewports.activeViewportIndex);

      if (enabledElement) {
        let viewport = cornerstone.getViewport(enabledElement);
        viewport.rotation += rotation;
        cornerstone.setViewport(enabledElement, viewport);
      }
    },
    flipViewportHorizontal: ({ viewports }) => {
      const enabledElement = getEnabledElement(viewports.activeViewportIndex);

      if (enabledElement) {
        let viewport = cornerstone.getViewport(enabledElement);
        viewport.hflip = !viewport.hflip;
        cornerstone.setViewport(enabledElement, viewport);
      }
    },
    flipViewportVertical: ({ viewports }) => {
      const enabledElement = getEnabledElement(viewports.activeViewportIndex);

      if (enabledElement) {
        let viewport = cornerstone.getViewport(enabledElement);
        viewport.vflip = !viewport.vflip;
        cornerstone.setViewport(enabledElement, viewport);
      }
    },
    scaleViewport: ({ direction, viewports }) => {
      const enabledElement = getEnabledElement(viewports.activeViewportIndex);
      const step = direction * 0.15;

      if (enabledElement) {
        if (step) {
          let viewport = cornerstone.getViewport(enabledElement);
          viewport.scale += step;
          cornerstone.setViewport(enabledElement, viewport);
        } else {
          cornerstone.fitToWindow(enabledElement);
        }
      }
    },
    resetViewport: ({ viewports }) => {
      const enabledElement = getEnabledElement(viewports.activeViewportIndex);

      if (enabledElement) {
        cornerstone.reset(enabledElement);
      }
    },
    invertViewport: ({ viewports }) => {
      const enabledElement = getEnabledElement(viewports.activeViewportIndex);

      if (enabledElement) {
        let viewport = cornerstone.getViewport(enabledElement);
        viewport.invert = !viewport.invert;
        cornerstone.setViewport(enabledElement, viewport);
      }
    },
    // TODO: this is receiving `evt` from `ToolbarRow`. We could use it to have
    //       better mouseButtonMask sets.
    setToolActive: ({ toolName }) => {
      if (!toolName) {
        console.warn('No toolname provided to setToolActive command');
      }
      cornerstoneTools.setToolActive(toolName, { mouseButtonMask: 1 });
    },
    clearAnnotations: ({ viewports }) => {
      const element = getEnabledElement(viewports.activeViewportIndex);
      if (!element) {
        return;
      }

      const enabledElement = cornerstone.getEnabledElement(element);
      if (!enabledElement || !enabledElement.image) {
        return;
      }

      const {
        toolState,
      } = cornerstoneTools.globalImageIdSpecificToolStateManager;
      if (
        !toolState ||
        toolState.hasOwnProperty(enabledElement.image.imageId) === false
      ) {
        return;
      }

      const imageIdToolState = toolState[enabledElement.image.imageId];

      const measurementsToRemove = [];

      Object.keys(imageIdToolState).forEach(toolType => {
        const { data } = imageIdToolState[toolType];

        data.forEach(measurementData => {
          const {
            _id,
            lesionNamingNumber,
            measurementNumber,
          } = measurementData;
          if (!_id) {
            return;
          }

          measurementsToRemove.push({
            toolType,
            _id,
            lesionNamingNumber,
            measurementNumber,
          });
        });
      });

      measurementsToRemove.forEach(measurementData => {
        OHIF.measurements.MeasurementHandlers.onRemoved({
          detail: {
            toolType: measurementData.toolType,
            measurementData,
          },
        });
      });
    },
    nextImage: ({ viewports }) => {
      const enabledElement = getEnabledElement(viewports.activeViewportIndex);
      scroll(enabledElement, 1);
    },
    previousImage: ({ viewports }) => {
      const enabledElement = getEnabledElement(viewports.activeViewportIndex);
      scroll(enabledElement, -1);
    },
    getActiveViewportEnabledElement: ({ viewports }) => {
      const enabledElement = getEnabledElement(viewports.activeViewportIndex);
      return enabledElement;
    },
    showDownloadViewportModal: ({ title, viewports }) => {
      const activeViewportIndex = viewports.activeViewportIndex;
      const { UIModalService } = servicesManager.services;
      if (UIModalService) {
        UIModalService.show({
          content: CornerstoneViewportDownloadForm,
          title,
          contentProps: {
            activeViewportIndex,
            onClose: UIModalService.hide,
          },
        });
      }
    },
    updateTableWithNewMeasurementData({
      toolType,
      measurementNumber,
      location,
      description,
    }) {
      // Update all measurements by measurement number
      const measurementApi = OHIF.measurements.MeasurementApi.Instance;
      const measurements = measurementApi.tools[toolType].filter(
        m => m.measurementNumber === measurementNumber
      );

      measurements.forEach(measurement => {
        measurement.location = location;
        measurement.description = description;

        measurementApi.updateMeasurement(measurement.toolType, measurement);
      });

      measurementApi.syncMeasurementsAndToolData();

      refreshCornerstoneViewports();
    },
    getNearbyToolData({ element, canvasCoordinates, availableToolTypes }) {
      const nearbyTool = {};
      let pointNearTool = false;

      availableToolTypes.forEach(toolType => {
        const elementToolData = cornerstoneTools.getToolState(
          element,
          toolType
        );

        if (!elementToolData) {
          return;
        }

        elementToolData.data.forEach((toolData, index) => {
          let elementToolInstance = cornerstoneTools.getToolForElement(
            element,
            toolType
          );

          if (!elementToolInstance) {
            elementToolInstance = cornerstoneTools.getToolForElement(
              element,
              `${toolType}Tool`
            );
          }

          if (!elementToolInstance) {
            console.warn('Tool not found.');
            return undefined;
          }

          if (
            elementToolInstance.pointNearTool(
              element,
              toolData,
              canvasCoordinates
            )
          ) {
            pointNearTool = true;
            nearbyTool.tool = toolData;
            nearbyTool.index = index;
            nearbyTool.toolType = toolType;
          }
        });

        if (pointNearTool) {
          return false;
        }
      });

      return pointNearTool ? nearbyTool : undefined;
    },
    removeToolState: ({ element, toolType, tool }) => {
      cornerstoneTools.removeToolState(element, toolType, tool);
      cornerstone.updateImage(element);
    },
    setCornerstoneLayout: () => {
      setCornerstoneLayout();
    },
    setWindowLevel: ({ viewports, window, level }) => {
      const enabledElement = getEnabledElement(viewports.activeViewportIndex);

      if (enabledElement) {
        let viewport = cornerstone.getViewport(enabledElement);

        viewport.voi = {
          windowWidth: Number(window),
          windowCenter: Number(level),
        };
        cornerstone.setViewport(enabledElement, viewport);
      }
    },
    fullScreen: ({ viewports }) => {
      const enabledElement = getEnabledElement(viewports.activeViewportIndex);

      if (enabledElement) {
        if (enabledElement.requestFullscreen) {
          enabledElement.requestFullscreen();
        } else if (enabledElement.webkitRequestFullscreen) {
          /* Safari */
          enabledElement.webkitRequestFullscreen();
        } else {
          throw new Error('Full screen is not supported by browser.');
        }
      }
    },
    pngViewer: () => {
      const ignoredElements = [
        'bottom-right overlay-element',
        'bottom-left2 warning',
        'bottom-left3 warning',
        'bottom-left overlay-element',
        'scroll',
      ];
      html2canvas(document.querySelector('.ViewerMain'), {
        ignoreElements: element => ignoredElements.includes(element.className),
      }).then(canvas => {
        const url = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = url;
        link.download = 'NUBIX - Estudio.png';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    },
    pdfViewer: () => {
      const ignoredElements = [
        'bottom-right overlay-element',
        'bottom-left2 warning',
        'bottom-left3 warning',
        'bottom-left overlay-element',
        'scroll',
      ];
      html2canvas(document.querySelector('.ViewerMain'), {
        ignoreElements: element => ignoredElements.includes(element.className),
      }).then(canvas => {
        let orientation = 'landscape';
        if (canvas.height > canvas.width) orientation = 'portrait';
        const doc = new jsPDF({
          format: [canvas.width, canvas.height],
          orientation: orientation,
          unit: 'px',
        });
        doc.addImage(
          canvas.toDataURL('image/png'),
          0,
          0,
          canvas.width,
          canvas.height,
          'NONE'
        );
        const pdf = doc.output('blob');
        const url = URL.createObjectURL(pdf);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'NUBIX - Estudio.pdf';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    },
    printViewer: async ({ viewports }) => {
      const { UINotificationService } = servicesManager.services;
      const ignoredElements = [
        'bottom-right overlay-element',
        'bottom-left2 warning',
        'bottom-left3 warning',
        'bottom-left overlay-element',
        'scroll',
      ];

      try {
        const canvas = await html2canvas(
          document.querySelector('.ViewerMain'),
          {
            ignoreElements: element =>
              ignoredElements.includes(element.className),
          }
        );

        const html = buildPrintDocument({
          header: getPrintHeader(
            _getActiveImageId(viewports.activeViewportIndex),
            _getActiveDisplaySetData(viewports)
          ),
          images: [canvas.toDataURL('image/jpeg', 0.92)],
          columns: 1,
          rows: 1,
          orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        });

        await openPrintSheet(html);
      } catch (error) {
        console.error('Print: could not print the current layout', error);
        UINotificationService.show({
          title: 'Imprimir',
          message: 'No se pudo preparar la impresión',
          type: 'error',
        });
      }
    },
    printSeries: ({ viewports }) => {
      const {
        UIModalService,
        UINotificationService,
      } = servicesManager.services;
      const displaySetData = _getActiveDisplaySetData(viewports);
      const imageIds = _getImageIds(displaySetData);

      if (!imageIds.length) {
        UINotificationService.show({
          title: 'Imprimir serie',
          message: 'Esta serie no tiene imágenes que imprimir',
          type: 'error',
        });
        return;
      }

      const { columns, rows } = getSeriesGrid(imageIds.length);
      const pages = Math.ceil(imageIds.length / (columns * rows));

      const print = async () => {
        UINotificationService.show({
          title: 'Imprimir serie',
          message: 'Preparando hojas…',
          type: 'info',
        });

        try {
          const enabledElement = getEnabledElement(
            viewports.activeViewportIndex
          );
          let viewport;
          if (enabledElement) {
            const { voi, invert } = cornerstone.getViewport(enabledElement);
            viewport = { voi, invert };
          }

          const images = await captureDisplaySetImages({
            imageIds,
            viewport,
            // A 4x5 cell is about 40mm wide, so 512px is already above 300dpi there.
            size: columns > 2 ? 512 : 1024,
          });

          const html = buildPrintDocument({
            header: getPrintHeader(imageIds[0], displaySetData),
            images,
            columns,
            rows,
          });

          await openPrintSheet(html);
        } catch (error) {
          console.error('Print: could not print series', error);
          UINotificationService.show({
            title: 'Imprimir serie',
            message: 'No se pudo preparar la impresión',
            type: 'error',
          });
        }
      };

      if (pages > MAX_PAGES_WITHOUT_CONFIRM) {
        UIModalService.show({
          content: PrintConfirmDialog,
          title: 'Imprimir serie',
          contentProps: {
            message: `Esta serie tiene ${imageIds.length} imágenes: se imprimirán ${pages} hojas.`,
            onConfirm: print,
          },
        });
        return;
      }

      return print();
    },
    hideOverlay: () => {
      document
        .querySelectorAll('.OHIFCornerstoneViewportOverlay')
        .forEach(element => {
          element.style.visibility =
            element.style.visibility === 'hidden' ? 'visible' : 'hidden';
        });
    },
    jumpToImage: ({
      StudyInstanceUID,
      SOPInstanceUID,
      frameIndex,
      activeViewportIndex,
      refreshViewports = true,
    }) => {
      const study = studyMetadataManager.get(StudyInstanceUID);

      if (activeViewportIndex >= 0) {
        const displaySet = study.findDisplaySet(ds => {
          return (
            ds.images &&
            ds.images.find(i => i.getSOPInstanceUID() === SOPInstanceUID)
          );
        });

        if (!displaySet) {
          return;
        }

        displaySet.SOPInstanceUID = SOPInstanceUID;
        displaySet.frameIndex = frameIndex;

        window.store.dispatch(
          setViewportSpecificData(activeViewportIndex, displaySet)
        );
      }

      if (refreshViewports) {
        refreshCornerstoneViewports();
      }
    },
  };

  const definitions = {
    jumpToImage: {
      commandFn: actions.jumpToImage,
      storeContexts: [],
      options: {},
    },
    getNearbyToolData: {
      commandFn: actions.getNearbyToolData,
      storeContexts: [],
      options: {},
    },
    removeToolState: {
      commandFn: actions.removeToolState,
      storeContexts: [],
      options: {},
    },
    updateTableWithNewMeasurementData: {
      commandFn: actions.updateTableWithNewMeasurementData,
      storeContexts: [],
      options: {},
    },
    showDownloadViewportModal: {
      commandFn: actions.showDownloadViewportModal,
      storeContexts: ['viewports'],
      options: {},
    },
    getActiveViewportEnabledElement: {
      commandFn: actions.getActiveViewportEnabledElement,
      storeContexts: ['viewports'],
      options: {},
    },
    rotateViewportCW: {
      commandFn: actions.rotateViewport,
      storeContexts: ['viewports'],
      options: { rotation: 90 },
    },
    rotateViewportCCW: {
      commandFn: actions.rotateViewport,
      storeContexts: ['viewports'],
      options: { rotation: -90 },
    },
    invertViewport: {
      commandFn: actions.invertViewport,
      storeContexts: ['viewports'],
      options: {},
    },
    flipViewportVertical: {
      commandFn: actions.flipViewportVertical,
      storeContexts: ['viewports'],
      options: {},
    },
    flipViewportHorizontal: {
      commandFn: actions.flipViewportHorizontal,
      storeContexts: ['viewports'],
      options: {},
    },
    scaleUpViewport: {
      commandFn: actions.scaleViewport,
      storeContexts: ['viewports'],
      options: { direction: 1 },
    },
    scaleDownViewport: {
      commandFn: actions.scaleViewport,
      storeContexts: ['viewports'],
      options: { direction: -1 },
    },
    fitViewportToWindow: {
      commandFn: actions.scaleViewport,
      storeContexts: ['viewports'],
      options: { direction: 0 },
    },
    resetViewport: {
      commandFn: actions.resetViewport,
      storeContexts: ['viewports'],
      options: {},
    },
    clearAnnotations: {
      commandFn: actions.clearAnnotations,
      storeContexts: ['viewports'],
      options: {},
    },
    nextImage: {
      commandFn: actions.nextImage,
      storeContexts: ['viewports'],
      options: {},
    },
    previousImage: {
      commandFn: actions.previousImage,
      storeContexts: ['viewports'],
      options: {},
    },
    // TOOLS
    setToolActive: {
      commandFn: actions.setToolActive,
      storeContexts: [],
      options: {},
    },
    setZoomTool: {
      commandFn: actions.setToolActive,
      storeContexts: [],
      options: { toolName: 'Zoom' },
    },
    setCornerstoneLayout: {
      commandFn: actions.setCornerstoneLayout,
      storeContexts: [],
      options: {},
      context: 'VIEWER',
    },
    setWindowLevel: {
      commandFn: actions.setWindowLevel,
      storeContexts: ['viewports'],
      options: {},
    },
    fullScreen: {
      commandFn: actions.fullScreen,
      storeContexts: ['viewports'],
      options: {},
    },
    pngViewer: {
      commandFn: actions.pngViewer,
      storeContexts: [],
      options: {},
    },
    pdfViewer: {
      commandFn: actions.pdfViewer,
      storeContexts: [],
      options: {},
    },
    printViewer: {
      commandFn: actions.printViewer,
      storeContexts: ['viewports'],
      options: {},
    },
    printSeries: {
      commandFn: actions.printSeries,
      storeContexts: ['viewports'],
      options: {},
    },
    hideOverlay: {
      commandFn: actions.hideOverlay,
      storeContexts: [],
      options: {},
    },
  };

  return {
    actions,
    definitions,
    defaultContext: 'ACTIVE_VIEWPORT::CORNERSTONE',
  };
};

export default commandsModule;

function isOnMobile() {
  let check = false;
  (function(a) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        a
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      check = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
}
